import React from "react";
import PropTypes from "prop-types";
import { TableBody, TableRow, TableCell, Checkbox } from "@mui/material";
import EmptyState from "@/components/molecules/EmptyState/EmptyState";
import ShimmerTableRow from "@/components/atoms/ShimmerTableRow";
import { LinkText, Tag } from "@/components/atoms";
import { COLOR_CODE, TAG_COLOR } from "@/utils/constants/colors";

const getColorTag = (status) => {
  const statusLower = status.toLocaleLowerCase();
  if (statusLower === "completed" || statusLower === "partially_delivered") {
    return "text-green-300";
  } else if (statusLower === "cancelled") {
    return "text-red-300";
  } else {
    return "text-yellow-300";
  }
};

// Helper component for rendering a single table cell
const RenderTableCell = ({ row, column, rowIndex, colIndex }) => {
  if (typeof column.render === "function") {
    return column.render(row, column, rowIndex, colIndex);
  }

  if (column.linkHandler) {
    return (
      <LinkText
        text={
          column.linkLabel
            ? column.linkLabel(row, column, rowIndex, colIndex)
            : row[column.name_of_db_column] || "\u2014"
        }
        id={`link-${rowIndex}-${column.name_of_db_column}`}
        handleClick={() => column.linkHandler(row, column, rowIndex, colIndex)}
      />
    );
  }

  if (column.downloadHandler) {
    return row.status === "Draft" ? (
      <div className="text-gray-500">
        {column.downloadLabel
          ? column.downloadLabel(row, column, rowIndex, colIndex)
          : "Download"}
      </div>
    ) : (
      <div>
        <LinkText
          text={
            column.downloadLabel
              ? column.downloadLabel(row, column, rowIndex, colIndex)
              : "Download"
          }
          id={row.productId}
          handleClick={() =>
            column.downloadHandler(row, column, rowIndex, colIndex)
          }
        />
      </div>
    );
  }

  if (column.name_of_db_column === "includeExclude") {
    return (
      <Tag
        text={row?.included ? "Exclude" : "Include"}
        color={row?.included ? TAG_COLOR.RED_150 : TAG_COLOR.GREEN_150}
        onClick={() => {}} // Placeholder for actual click handler
      />
    );
  }

  return (
    <div
      style={{
        overflow: "hidden",
        width: "100%",
        whiteSpace: "normal",
        wordBreak: "break-word",
        paddingLeft: "8px",
      }}
      className={
        column?.colorCode && column.className
          ? getColorTag(column.className(row, column, rowIndex, colIndex))
          : ""
      }
    >
      {row[column.name_of_db_column] === null ||
      row[column.name_of_db_column] === undefined
        ? "—"
        : row[column.name_of_db_column]}
    </div>
  );
};

// Helper component for rendering a single table row
const RenderTableRow = ({
  row,
  columns,
  rowIndex,
  selectedRows,
  handleRowSelect,
}) => {
  const isSelected = selectedRows.some(
    (selectedRow) =>
      (selectedRow.productId ?? selectedRow.orderId ?? selectedRow.item) ===
      (row.productId ?? row.orderId ?? row.item)
  );

  return (
    <TableRow
      key={`row-${rowIndex}`}
      selected={isSelected}
      sx={{
        "&.Mui-selected": {
          backgroundColor: `${COLOR_CODE._383838} !important`,
        },
        "&:hover": {
          backgroundColor: COLOR_CODE._383838,
        },
        "& .MuiTableCell-root": {
          borderBottom: "2px solid rgba(81, 81, 81, 1)",
          padding: "5px",
        },
      }}
    >
      <TableCell padding="checkbox">
        <Checkbox
          size="small"
          className="custom-checkbox"
          checked={isSelected}
          onChange={() => handleRowSelect(row)}
          inputProps={{ "aria-label": "select row" }}
        />
      </TableCell>
      {columns.map((column, colIndex) => (
        <TableCell key={`cell-${colIndex}`} align="left">
          <RenderTableCell
            row={row}
            column={column}
            rowIndex={rowIndex}
            colIndex={colIndex}
          />
        </TableCell>
      ))}
      <TableCell
        sx={{
          width: "60px",
          borderBottom: "2px solid rgba(81, 81, 81, 1)",
          padding: "5px",
        }}
      />
    </TableRow>
  );
};

const DataTableBody = ({
  isLoading,
  records,
  columns,
  selectedRows,
  handleRowSelect,
  handleResetAll,
}) => (
  <TableBody>
    {isLoading &&
      Array.from({ length: 12 }).map((_, index) => (
        <ShimmerTableRow
          key={`loading-${index}`}
          columnCount={columns.length + 1}
        />
      ))}
    {!isLoading && records?.length === 0 && (
      <TableRow className="empty-state-row">
        <TableCell
          colSpan={columns.length + 1}
          align="center"
          sx={{
            backgroundColor: COLOR_CODE._1D1D1D,
            "&:hover": {
              backgroundColor: COLOR_CODE._1D1D1D,
            },
            "& .MuiTableRow-root:hover": {
              backgroundColor: COLOR_CODE._1D1D1D,
            },
          }}
        >
          <EmptyState onActionClick={handleResetAll} />
        </TableCell>
      </TableRow>
    )}
    {!isLoading &&
      records?.length > 0 &&
      records.map((row, rowIndex) => (
        <RenderTableRow
          key={`row-${rowIndex}`}
          row={row}
          columns={columns}
          rowIndex={rowIndex}
          selectedRows={selectedRows}
          handleRowSelect={handleRowSelect}
        />
      ))}
  </TableBody>
);

DataTableBody.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  records: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  selectedRows: PropTypes.array.isRequired,
  handleRowSelect: PropTypes.func.isRequired,
  handleResetAll: PropTypes.func.isRequired,
};

export default DataTableBody;